import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ABILITY_INFO, DIRS8, QUALITY_INFO, ability, abilityBlockReason, actionKey, chebyshev, inCheck, offset, parseSq,
  type AbilityKind, type Action, type Color, type GameEvent, type GameState, type MoveQuality,
} from '@ic/engine';
import { Icon } from './Icons';
import { PieceIcon } from './Pieces';
import { settings, useStore } from '../lib/store';
import { sfx as realSfx } from '../lib/sound';

/** Stand-in used by boards that must stay quiet (the home page showcase). */
const quiet = new Proxy({}, { get: () => () => {} }) as typeof realSfx;

export interface EditorHooks {
  onSquare(sq: number): void;
  onMovePiece(from: number, to: number): void;
  onDropData(sq: number, data: string): void;
}

export interface BoardProps {
  state: GameState;
  orientation: Color;
  legal?: Action[];
  interactive?: boolean;
  onAction?: (a: Action) => void;
  lastAction?: Action | null;
  events?: GameEvent[];
  eventsKey?: number;
  editor?: EditorHooks;
  marks?: Record<number, string>;
  /** A move being considered (bot thinking), drawn as a faint arrow. */
  ghost?: { action: Action; final?: boolean } | null;
  /** Rating of the last move, shown on its destination square. */
  quality?: { sq: number; q: MoveQuality; key: string } | null;
  /** No sounds (showcase boards). */
  silent?: boolean;
  /** Square picking (admin spawn): clicks go to `onPick` for squares where `can` is true. */
  pick?: { can: (sq: number) => boolean; onPick: (sq: number) => void } | null;
}

interface Fx { id: number; ev: GameEvent; delay: number }
let fxId = 0;

export function Board(props: BoardProps) {
  const { state: s, orientation, legal = [], interactive = false, onAction, lastAction, events, eventsKey, editor, marks, ghost, quality, pick, silent } = props;
  const sfx = silent ? quiet : realSfx;
  const set = useStore(settings);
  const ref = useRef<HTMLDivElement>(null);
  const [sel, setSel] = useState<number | null>(null);
  const [mode, setMode] = useState<AbilityKind | null>(null);
  const [preview, setPreview] = useState<AbilityKind | null>(null);
  const [promo, setPromo] = useState<Action[] | null>(null);
  const [drag, setDrag] = useState<{ from: number; x: number; y: number } | null>(null);
  const dragRef = useRef<{ from: number; wasSel: boolean; moved: boolean; x0: number; y0: number } | null>(null);
  const [fx, setFx] = useState<Fx[]>([]);
  const { w, h } = s;

  const flip = orientation === 'b';
  const colOf = (sq: number) => (flip ? w - 1 - (sq % w) : sq % w);
  const rowOf = (sq: number) => (flip ? Math.floor(sq / w) : h - 1 - Math.floor(sq / w));
  const sqAt = (col: number, row: number) => (flip ? row * w + (w - 1 - col) : (h - 1 - row) * w + col);

  // Reset selection whenever the position changes.
  useEffect(() => { setSel(null); setMode(null); setPromo(null); }, [s.ply, s.result, s.rs]);

  // Effects + sounds for the latest batch of events.
  const slideFrom = useMemo(() => {
    const m = new Map<number, number>();
    for (const e of events ?? []) {
      if (e.e === 'move' || e.e === 'tunnel' || e.e === 'push' || e.e === 'castle') { if (e.from !== e.to) m.set(e.to, e.from); }
      else if (e.e === 'tongue') m.set(e.to, e.target);
      else if (e.e === 'stampede' || e.e === 'survive') m.set(e.to, e.from);
    }
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventsKey]);

  useEffect(() => {
    if (!events?.length) return;
    const list: Fx[] = events.map((ev) => ({ id: ++fxId, ev, delay: 'step' in ev ? ev.step * 320 : 0 }));
    if (set.animations) setFx(list);
    const kinds = new Set(events.map((e) => e.e));
    if (kinds.has('explode') || kinds.has('mineTrigger')) sfx.explode();
    else if (kinds.has('fire')) sfx.fire();
    else if (kinds.has('sweep') || kinds.has('tongue') || kinds.has('trumpet')) sfx.whoosh();
    else if (kinds.has('capture') || kinds.has('destroy')) sfx.capture();
    else if (kinds.has('minePlace') || kinds.has('dig') || kinds.has('defuse') || kinds.has('hedge') || kinds.has('tend') || kinds.has('spawn') || kinds.has('feast') || kinds.has('pan')) sfx.mine();
    else sfx.move();
    if (inCheck(s)) setTimeout(sfx.check, 120);
    const t = setTimeout(() => setFx([]), 2400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventsKey]);

  useEffect(() => {
    if (quality?.q === 'brilliant' || quality?.q === 'great') sfx.brilliant();
    else if (quality?.q === 'blunder') sfx.blunder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quality?.key]);

  const fromSel = sel == null ? [] : legal.filter((a) => a.from === sel);
  const moveTargets = new Map<number, Action[]>();
  for (const a of fromSel) if (a.t === 'move') moveTargets.set(a.to, [...(moveTargets.get(a.to) ?? []), a]);
  const abilityTargets = new Map<number, Action>();
  if (mode) for (const a of fromSel) if (a.t === 'ability' && a.kind === mode && a.target != null) abilityTargets.set(a.target, a);

  const selPiece = sel != null ? s.board[sel] : null;
  const selDef = selPiece ? s.rs.pieces[selPiece.type] : undefined;
  const ringAbilities = selDef?.abilities.filter((a) => a.enabled) ?? [];
  const RING_R = 1.2;
  let ringStart = -Math.PI / 2;
  let ringStep = 0;
  let ringCenter = -Math.PI / 2;
  if (sel != null && ringAbilities.length) {
    const n = ringAbilities.length;
    const toMid = Math.atan2((h - 1) / 2 - rowOf(sel), (w - 1) / 2 - colOf(sel));
    const nearEdge = colOf(sel) < 1 || rowOf(sel) < 1 || colOf(sel) > w - 2 || rowOf(sel) > h - 2;
    const center = nearEdge ? toMid : -Math.PI / 2;
    const arc = n === 1 ? 0 : Math.min(2 * Math.PI * (1 - 1 / n), (n - 1) * (Math.PI / 3.2));
    ringStep = n === 1 ? 0 : arc / (n - 1);
    ringStart = center - arc / 2;
    ringCenter = center;
  }
  // Caption sits on the side of the piece away from the buttons.
  // Caption sits just beyond the buttons, clamped inside the board.
  const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
  const capPos = sel == null ? {} : {
    left: `${(clamp(colOf(sel) + 0.5 + Math.cos(ringCenter) * 2.25, 1.2, w - 1.2) / w) * 100}%`,
    top: `${(clamp(rowOf(sel) + 0.5 + Math.sin(ringCenter) * 2.25, 0.5, h - 0.5) / h) * 100}%`,
  };

  // Escape backs out of ability targeting, then out of the selection.
  useEffect(() => {
    if (sel == null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (mode) setMode(null);
      else setSel(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sel, mode]);

  // Area preview for the hovered/active ability.
  const area = new Set<number>();
  const shown = preview ?? mode;
  if (sel != null && shown) {
    const ab = ability(selDef, shown);
    if (ab) {
      if (shown === 'fireBreath' || shown === 'tunnel' || shown === 'tongue' || shown === 'stampede' || shown === 'panToss') {
        for (const [dx, dy] of shown === 'stampede' ? DIRS8.slice(0, 4) : DIRS8)
          for (let d = shown === 'tunnel' ? ab.range : 1; d <= ab.range; d++) {
            const t = offset(sel, dx * d, dy * d, w, h);
            if (t >= 0) area.add(t);
          }
      } else {
        const r = shown === 'wingSweep' || shown === 'kick' ? 1 : ab.range;
        for (let sq = 0; sq < w * h; sq++) if (sq !== sel && chebyshev(sq, sel, w) <= r) area.add(sq);
      }
    }
  }

  const act = (a: Action) => {
    setSel(null); setMode(null); setPromo(null); setPreview(null);
    onAction?.(a);
  };
  const doMoves = (moves: Action[]) => (moves.length > 1 ? setPromo(moves) : act(moves[0]));

  const pointToSq = (clientX: number, clientY: number) => {
    const r = ref.current!.getBoundingClientRect();
    const col = Math.floor(((clientX - r.left) / r.width) * w);
    const row = Math.floor(((clientY - r.top) / r.height) * h);
    if (col < 0 || row < 0 || col >= w || row >= h) return -1;
    return sqAt(col, row);
  };

  const canMoveFrom = (sq: number) => legal.some((a) => a.from === sq);

  function onPointerDown(e: React.PointerEvent) {
    if (e.button !== 0 || promo) return;
    const sq = pointToSq(e.clientX, e.clientY);
    if (sq < 0) return;
    if (pick) { if (pick.can(sq)) pick.onPick(sq); return; }
    if (editor) {
      if (s.board[sq]) {
        dragRef.current = { from: sq, wasSel: false, moved: false, x0: e.clientX, y0: e.clientY };
        ref.current!.setPointerCapture(e.pointerId);
      } else editor.onSquare(sq);
      return;
    }
    if (!interactive) return;
    if (sel != null) {
      const at = mode ? abilityTargets.get(sq) : undefined;
      if (at) return act(at);
      const mv = moveTargets.get(sq);
      if (mv) return doMoves(mv);
    }
    if (canMoveFrom(sq)) {
      dragRef.current = { from: sq, wasSel: sel === sq, moved: false, x0: e.clientX, y0: e.clientY };
      setSel(sq);
      setMode(null);
      ref.current!.setPointerCapture(e.pointerId);
    } else {
      setSel(null);
      setMode(null);
    }
  }

  function onPointerMove(e: React.PointerEvent) {
    const d = dragRef.current;
    if (!d) return;
    if (!d.moved && Math.hypot(e.clientX - d.x0, e.clientY - d.y0) < 5) return;
    d.moved = true;
    const r = ref.current!.getBoundingClientRect();
    setDrag({ from: d.from, x: e.clientX - r.left, y: e.clientY - r.top });
  }

  function onPointerUp(e: React.PointerEvent) {
    const d = dragRef.current;
    dragRef.current = null;
    setDrag(null);
    if (!d) return;
    const to = pointToSq(e.clientX, e.clientY);
    if (editor) {
      if (d.moved) editor.onMovePiece(d.from, to);
      else editor.onSquare(d.from);
      return;
    }
    if (d.moved) {
      const mv = to >= 0 ? legal.filter((a) => a.t === 'move' && a.from === d.from && a.to === to) : [];
      if (mv.length) doMoves(mv);
    } else if (d.wasSel) {
      setSel(null);
    }
  }

  const lastSquares = new Set<number>();
  if (lastAction) {
    lastSquares.add(lastAction.from);
    if (lastAction.t === 'move') lastSquares.add(lastAction.to);
    else if (lastAction.target != null) lastSquares.add(lastAction.target);
  }
  const checked = new Set<number>();
  if (inCheck(s)) s.board.forEach((p, sq) => { if (p && p.color === s.turn && s.rs.pieces[p.type]?.royal) checked.add(sq); });
  const targets = new Set<number>();
  if (s.rs.rules.winConditions.reachSquare)
    for (const n of [...s.rs.rules.reachSquares.w, ...s.rs.rules.reachSquares.b]) targets.add(parseSq(n, w, h));

  const cells = [];
  for (let row = 0; row < h; row++) {
    for (let col = 0; col < w; col++) {
      const sq = sqAt(col, row);
      const x = sq % w;
      const y = Math.floor(sq / w);
      const cell = s.cells[sq];
      const hole = s.holes.find((o) => o.sq === sq);
      const burn = s.burning.find((o) => o.sq === sq);
      const hedge = s.hedges?.find((o) => o.sq === sq);
      const mine = s.mines.find((m) => m.sq === sq);
      const p = s.board[sq];
      const def = p ? s.rs.pieces[p.type] : undefined;
      const cls = ['sq', (x + y) % 2 === 0 ? 'dark' : 'light'];
      if (cell === 1) cls.push('void');
      if (cell === 2) cls.push('wall');
      if (hole) cls.push('hole');
      if (burn) cls.push('burning');
      if (hedge) cls.push('hedge');
      if (lastSquares.has(sq)) cls.push('last');
      if (sel === sq) cls.push('sel');
      if (checked.has(sq)) cls.push('check');
      if (area.has(sq)) cls.push('area');
      if (targets.has(sq)) cls.push('target');
      if (marks?.[sq]) cls.push(marks[sq]);
      if (pick?.can(sq)) cls.push('pick-target');
      const slide = p && slideFrom.has(sq) && set.animations ? slideFrom.get(sq)! : -1;
      const style = slide >= 0 ? ({ '--dx': colOf(slide) - col, '--dy': rowOf(slide) - row } as React.CSSProperties) : undefined;
      cells.push(
        <div key={sq} className={cls.join(' ')}>
          {set.coords && col === 0 && cell !== 1 && <span className="coord rank">{y + 1}</span>}
          {set.coords && row === h - 1 && cell !== 1 && <span className="coord file">{'abcdefghijklmnop'[x]}</span>}
          {hole && <span className="badge hole-left">{Math.ceil(hole.left / 2)}</span>}
          {hedge && <span className="badge hedge-left">{Math.ceil(hedge.left / 2)}</span>}
          {mine && <span className={`mine ${mine.owner} ${mine.revealed ? 'revealed' : ''}`} title="Mine" />}
          {p && def && (
            <div
              key={`${p.id}:${sq}`}
              className={`piece ${slide >= 0 ? 'slide' : ''} ${drag?.from === sq ? 'ghosted' : ''} ${p.fuse ? 'armed' : ''}`}
              style={style}
            >
              <PieceIcon icon={def.icon} color={p.color} set={set.pieceSet} />
              {p.fuse > 0 && <span className="badge fuse">{p.fuse}</span>}
              {p.exhausted > 0 && <span className="badge tired" title="Resting this turn">z</span>}
            </div>
          )}
          {moveTargets.has(sq) && !mode && (
            <span className={p || moveTargets.get(sq)!.some((a) => a.t === 'move' && a.epCapture != null) ? 'hint capture' : 'hint'} />
          )}
          {abilityTargets.has(sq) && <span className="hint ability" />}
        </div>,
      );
    }
  }

  const dragPiece = drag ? s.board[drag.from] : null;
  const pct = (sq: number) => ({ left: `${((colOf(sq) + 0.5) / w) * 100}%`, top: `${((rowOf(sq) + 0.5) / h) * 100}%` });

  return (
    <div className="board-wrap">
      <div
        ref={ref}
        className={`board theme-${set.boardTheme} ${editor ? 'editing' : ''}`}
        style={{ gridTemplateColumns: `repeat(${w}, 1fr)`, aspectRatio: `${w} / ${h}` }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={() => { dragRef.current = null; setDrag(null); }}
        onDragOver={editor ? (e) => e.preventDefault() : undefined}
        onDrop={editor ? (e) => {
          e.preventDefault();
          const sq = pointToSq(e.clientX, e.clientY);
          if (sq >= 0) editor.onDropData(sq, e.dataTransfer.getData('text/plain'));
        } : undefined}
      >
        {cells}
        <div className="fx-layer">
          {fx.map(({ id, ev, delay }) => {
            const style: React.CSSProperties = { animationDelay: `${delay}ms` };
            switch (ev.e) {
              case 'fire': {
                const dx = colOf(ev.to) - colOf(ev.from);
                const dy = rowOf(ev.to) - rowOf(ev.from);
                const len = Math.hypot(dx, dy);
                return <div key={id} className="fx fire" style={{ ...pct(ev.from), width: `${(len / w) * 100}%`, height: `${(0.45 / h) * 100}%`, transform: `translateY(-50%) rotate(${Math.atan2(dy, dx)}rad)` }} />;
              }
              case 'explode':
                return <div key={id} className="fx explode" style={{ ...style, ...pct(ev.sq), width: `${((ev.radius * 2 + 1) / w) * 100}%`, height: `${((ev.radius * 2 + 1) / h) * 100}%` }} />;
              case 'sweep':
                return <div key={id} className="fx sweep" style={{ ...pct(ev.sq), width: `${(3 / w) * 100}%`, height: `${(3 / h) * 100}%` }} />;
              case 'destroy':
                return <div key={id} className={`fx destroy ${ev.cause}`} style={{ ...style, ...pct(ev.sq), width: `${100 / w}%`, height: `${100 / h}%` }} />;
              case 'minePlace':
              case 'dig':
              case 'defuse':
              case 'mineTrigger':
              case 'arm':
              case 'promote':
                return <div key={id} className={`fx ${ev.e}`} style={{ ...pct(ev.sq), width: `${100 / w}%`, height: `${100 / h}%` }} />;
              case 'tongue': {
                const dx = colOf(ev.target) - colOf(ev.from);
                const dy = rowOf(ev.target) - rowOf(ev.from);
                return <div key={id} className="fx tongue" style={{ ...pct(ev.from), width: `${(Math.hypot(dx, dy) / w) * 100}%`, height: `${(0.16 / h) * 100}%`, transform: `translateY(-50%) rotate(${Math.atan2(dy, dx)}rad)` }} />;
              }
              case 'pan': {
                const dx = colOf(ev.target) - colOf(ev.from);
                const dy = rowOf(ev.target) - rowOf(ev.from);
                return <div key={id} className="fx tongue pan" style={{ ...pct(ev.from), width: `${(Math.hypot(dx, dy) / w) * 100}%`, height: `${(0.22 / h) * 100}%`, transform: `translateY(-50%) rotate(${Math.atan2(dy, dx)}rad)` }} />;
              }
              case 'spawn':
                return <div key={id} className="fx hedge spawn" style={{ ...pct(ev.sq), width: `${100 / w}%`, height: `${100 / h}%` }} />;
              case 'survive':
                return <div key={id} className="fx destroy survive" style={{ ...pct(ev.from), width: `${100 / w}%`, height: `${100 / h}%` }} />;
              case 'feast':
                return <div key={id} className="fx sweep feast" style={{ ...pct(ev.sq), width: `${(3 / w) * 100}%`, height: `${(3 / h) * 100}%` }} />;
              case 'trumpet':
                return <div key={id} className="fx sweep trumpet" style={{ ...pct(ev.sq), width: `${(3 / w) * 100}%`, height: `${(3 / h) * 100}%` }} />;
              case 'hedge':
              case 'tend':
                return <div key={id} className={`fx ${ev.e}`} style={{ ...pct(ev.sq), width: `${100 / w}%`, height: `${100 / h}%` }} />;
              case 'tunnel':
                return <div key={id} className="fx dig" style={{ ...pct(ev.from), width: `${100 / w}%`, height: `${100 / h}%` }} />;
              default:
                return null;
            }
          })}
        </div>
        {quality && (() => {
          const info = QUALITY_INFO[quality.q];
          const loud = quality.q === 'brilliant' || quality.q === 'great' || quality.q === 'blunder' || quality.q === 'mistake' || quality.q === 'miss';
          return (
            <div key={quality.key} className="q-layer" style={{ left: `${(colOf(quality.sq) / w) * 100}%`, top: `${(rowOf(quality.sq) / h) * 100}%`, width: `${100 / w}%`, height: `${100 / h}%` }}>
              <span className={`q-mark q-${quality.q}`} style={{ background: info.color }} title={info.label}>{info.symbol}</span>
              {loud && set.animations && <span className="q-pop" style={{ color: info.color }}>{info.label}{quality.q === 'brilliant' ? '!!' : quality.q === 'blunder' ? '??' : ''}</span>}
            </div>
          );
        })()}
        {ghost && (() => {
          const a = ghost.action;
          const to = a.t === 'move' ? a.to : a.target ?? a.from;
          const [x1, y1, x2, y2] = [colOf(a.from) + 0.5, rowOf(a.from) + 0.5, colOf(to) + 0.5, rowOf(to) + 0.5];
          const len = Math.hypot(x2 - x1, y2 - y1);
          const k = len ? (len - 0.28) / len : 0; // stop the line just before the target centre
          return (
            <svg key={actionKey(a)} className={`ghost-layer ${ghost.final ? 'final' : ''}`} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden="true">
              <defs>
                <marker id="ghost-head" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="3" markerHeight="3" orient="auto-start-reverse">
                  <path d="M0 0L10 5L0 10Z" />
                </marker>
              </defs>
              <circle className="ghost-origin" cx={x1} cy={y1} r={0.42} />
              {len > 0
                ? <line x1={x1} y1={y1} x2={x1 + (x2 - x1) * k} y2={y1 + (y2 - y1) * k} markerEnd="url(#ghost-head)" />
                : <circle className="ghost-self" cx={x1} cy={y1} r={0.3} />}
            </svg>
          );
        })()}
        {dragPiece && drag && (
          <div className="drag-piece" style={{ left: drag.x, top: drag.y, width: `${100 / w}%`, height: `${100 / h}%` }}>
            <PieceIcon icon={s.rs.pieces[dragPiece.type]?.icon ?? 'pawn'} color={dragPiece.color} set={set.pieceSet} />
          </div>
        )}
        {promo && (
          <div className="promo-overlay" onPointerDown={(e) => e.stopPropagation()}>
            <div className="promo-box">
              {promo.map((a) => a.t === 'move' && a.promo && (
                <button key={a.promo} onClick={() => act(a)} title={s.rs.pieces[a.promo]?.name}>
                  <PieceIcon icon={s.rs.pieces[a.promo]?.icon ?? 'queen'} color={s.turn} set={set.pieceSet} />
                </button>
              ))}
              <button className="promo-cancel" onClick={() => setPromo(null)} aria-label="Cancel"><Icon name="x" /></button>
            </div>
          </div>
        )}
      </div>
      {interactive && sel != null && selPiece && selDef && !promo && !drag && ringAbilities.length > 0 && (
        <div className="ring-layer" style={{ aspectRatio: `${w} / ${h}` }}>
          {mode ? (
            <div className="ring-caption active" style={capPos}>
              <Icon name={mode} size={15} />
              <span>{ABILITY_INFO[mode].name}: pick a target</span>
              <button type="button" className="ring-cancel" onClick={() => setMode(null)} aria-label="Cancel ability"><Icon name="x" size={13} /></button>
            </div>
          ) : (<>
            <div className="ring-halo" style={{ ...pct(sel), width: `${(2.9 / w) * 100}%`, height: `${(2.9 / h) * 100}%` }} />
            {ringAbilities.map((ab, i) => {
              const acts = fromSel.filter((a) => a.t === 'ability' && a.kind === ab.kind);
              const info = ABILITY_INFO[ab.kind];
              const cd = selPiece.cd[ab.kind] ?? 0;
              const ang = ringStart + i * ringStep;
              const style = {
                left: `${((colOf(sel) + 0.5 + Math.cos(ang) * RING_R) / w) * 100}%`,
                top: `${((rowOf(sel) + 0.5 + Math.sin(ang) * RING_R) / h) * 100}%`,
                animationDelay: `${i * 35}ms`,
              };
              const why = acts.length ? '' : abilityBlockReason(s, sel, ab.kind) ?? 'not available right now';
              return (
                <button
                  key={ab.kind}
                  type="button"
                  className={`ring-btn ${acts.length ? '' : 'off'}`}
                  style={style}
                  disabled={!acts.length}
                  aria-label={`${info.name}${acts.length ? '' : ` (${why})`}`}
                  onPointerEnter={() => setPreview(ab.kind)}
                  onPointerLeave={() => setPreview(null)}
                  onFocus={() => setPreview(ab.kind)}
                  onBlur={() => setPreview(null)}
                  onClick={() => { if (!info.targeted) act(acts[0]); else { setMode(ab.kind); setPreview(null); } }}
                >
                  <Icon name={ab.kind} size={20} />
                  {cd > 0 && <span className="ring-cd">{cd}</span>}
                </button>
              );
            })}
            <div className="ring-caption" style={capPos}>
              {preview ? <>
                <b>{ABILITY_INFO[preview].name}</b>
                <span>{(() => {
                  if (fromSel.some((a) => a.t === 'ability' && a.kind === preview)) return ABILITY_INFO[preview].targeted ? 'click, then pick a square' : 'click to use';
                  return abilityBlockReason(s, sel, preview) ?? 'not available right now';
                })()}</span>
              </> : <b>{selDef.name}</b>}
            </div>
          </>)}
        </div>
      )}
    </div>
  );
}
